/**
 * ARBETSORDRAR — listan. Prototypens data-screen="ordrar".
 *
 * ADRESSEN STÅR STÖRST, och det är inte en smaksak: en hantverkare känner igen
 * ett jobb på adressen, inte på ett ordernummer. Prototypens kort är byggt så,
 * och <Orderkort> har adressen som första prop av samma skäl.
 *
 * REMSAN FILTRERAR PÅ STATUS, INTE PÅ PERSON. shared/routes.ts är uttrycklig:
 * "'Mina arbetsordrar' är en SORTERING i klienten, inte ett filter." En anställd
 * montör får rapportera på VARJE levande arbetsorder (work_order i
 * schema/company.sql), så att gömma andras ordrar hade tagit bort jobb han
 * faktiskt ska kunna skriva timmar på. Mina ligger därför ÖVERST — och alla
 * andra ligger kvar under.
 *
 * PROTOTYPENS 'Idag / Veckan / Alla' GÅR INTE ATT BYGGA: en arbetsorder har
 * ingen dag i kontraktet. Den har varken planerat datum eller klockslag (se
 * work_order i schema/company.sql), och "08:00" på prototypens kort är
 * påklistrad data. Remsan filtrerar därför på det som FINNS — status open/done —
 * i stället för på ett fält som inte gör det. Se LUCKOR i överlämningen.
 *
 * INGET PRIS PÅ DEN HÄR SKÄRMEN, oavsett vem som tittar (beslut 16). Lagret bär
 * bara *WorkerRow-typerna, så det går inte ens att skriva ut ett av misstag.
 */

import { useState } from 'react';
import { Bricka, Felruta, Knapp, Laddar, Orderkort, Segment, Tomtext } from '../ui';
import { useInloggad } from '../app/session';
import { useNavigering } from '../app/navigering';
import { visaMinuter } from '../app/datum';
import {
  adressFor,
  kundMedId,
  medlemsnamn,
  rapporteratPa,
  useArbetsorderlager,
} from './arbetsorder-data';
import type { Arbetsorderlager } from './arbetsorder-data';
import type { WorkOrderWorkerRow } from '@delat/types';

type Orderfilter = 'oppna' | 'klara' | 'alla';

const FILTERVAL = [
  { varde: 'oppna', etikett: 'Öppna' },
  { varde: 'klara', etikett: 'Klara' },
  { varde: 'alla', etikett: 'Alla' },
] as const satisfies readonly { varde: Orderfilter; etikett: string }[];

const passarFiltret = (order: WorkOrderWorkerRow, filter: Orderfilter): boolean =>
  filter === 'alla' ? true : filter === 'oppna' ? order.status === 'open' : order.status === 'done';

/** Första raden i beskrivningen — den är både rubrik och att-göra-text. */
const rubrikrad = (beskrivning: string): string => {
  const rad = beskrivning.split('\n')[0]?.trim() ?? '';
  return rad.length > 90 ? `${rad.slice(0, 89)}...` : rad;
};

export const Ordrar = () => {
  const { firmaId, konto, arChef, arLast } = useInloggad();
  const { ga } = useNavigering();
  const { lage, laddaOm } = useArbetsorderlager(firmaId);
  const [filter, sattFilter] = useState<Orderfilter>('oppna');

  const listan = (lager: Arbetsorderlager): readonly WorkOrderWorkerRow[] =>
    lager.ordrar
      .filter((order) => passarFiltret(order, filter))
      .slice()
      .sort((a, b) => {
        // Mina först. Sorteringen, inte filtret — se filhuvudet.
        const minA = a.responsible_account_id === konto.id ? 0 : 1;
        const minB = b.responsible_account_id === konto.id ? 0 : 1;
        if (minA !== minB) return minA - minB;
        const adressA = adressFor(a, kundMedId(lager, a.customer_id));
        const adressB = adressFor(b, kundMedId(lager, b.customer_id));
        return adressA.localeCompare(adressB, 'sv');
      });

  // EN gång per rendering. Sorteringen slår upp kunden för varje jämförelse, och
  // tre anrop i JSX:en hade gjort om hela jobbet tre gånger.
  const lager = lage.status === 'klart' ? lage.lager : null;
  const synliga = lager === null ? [] : listan(lager);

  return (
    <>
      <Segment val={FILTERVAL} valt={filter} onValj={sattFilter} etikett="Filter" />

      {lage.status === 'laddar' && <Laddar rader={4} />}

      {lage.status === 'fel' && (
        <Felruta
          fel={lage.fel}
          atgard={
            <Knapp variant="piller" onClick={laddaOm}>
              Försök igen
            </Knapp>
          }
        />
      )}

      {lager !== null &&
        synliga.map((order) => {
          const kund = kundMedId(lager, order.customer_id);
          const ansvarig = medlemsnamn(lager, order.responsible_account_id);
          const gjort = rapporteratPa(lager, order.id);
          const min = order.responsible_account_id === konto.id;
          return (
            <Orderkort
              key={order.id}
              adress={adressFor(order, kund)}
              meta={rubrikrad(order.description)}
              fot={
                <>
                  {kund === null
                    ? 'Okänd kund'
                    : `${kund.name} · ${kund.kind === 'private' ? 'Privatkund' : 'Företagskund'}`}
                  {gjort.minuter > 0 && ` · ${visaMinuter(gjort.minuter)} rapporterat`}
                </>
              }
              bricka={
                order.status === 'done' ? (
                  <Bricka ton="klar">Klar</Bricka>
                ) : min ? (
                  <Bricka ton="tilldelad">Min</Bricka>
                ) : ansvarig === null ? (
                  <Bricka ton="pagaende">Ingen ansvarig</Bricka>
                ) : (
                  <Bricka ton="neutral">{ansvarig.split(' ')[0] ?? ansvarig}</Bricka>
                )
              }
              onClick={() => ga('order', { work_order_id: order.id })}
            />
          );
        })}

      {lager !== null && synliga.length === 0 && (
        <Tomtext>
          {filter === 'klara'
            ? 'Inget jobb är markerat klart än.'
            : 'Inga arbetsordrar i det här urvalet.'}
        </Tomtext>
      )}

      {lager !== null && lager.avkortat && (
        <Tomtext>
          Firman har fler rader än appen hämtar i ett svep. Listan kan vara
          ofullständig — ladda om för att hämta på nytt.
        </Tomtext>
      )}

      {arChef && (
        <div style={{ marginTop: '0.75rem' }}>
          <Knapp
            disabled={arLast}
            onClick={() => ga('ordny')}
            title={arLast ? 'Prenumerationen är pausad. Inget nytt kan sparas.' : undefined}
          >
            Ny arbetsorder
          </Knapp>
        </div>
      )}

      <Tomtext>
        Dina jobb ligger överst. Alla levande arbetsordrar står kvar under — du får
        rapportera på vilken som helst.
      </Tomtext>
    </>
  );
};
